import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import { cx, useUi } from "../../ui";
import FieldRow from "../../components/FieldRow";
import {
  DEFAULT_SHIFT,
  SHIFT_GRACE_PERIOD_OPTIONS,
  SHIFT_TIME_OPTIONS,
  SLOT_MINUTES_OPTIONS,
} from "../../shifts/constants";
import { formatTimeOfDay, validateShift } from "../../shifts/shiftHelpers";

const useStyles = makeStyles({
  card: {
    border: `1px solid ${T.borderSoft}`,
    borderRadius: 8,
    padding: "10px 12px 2px",
    marginBottom: 8,
  },
  inactive: { opacity: 0.6 },
  head: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    fontSize: 13,
    marginBottom: 8,
  },
  window: { fontWeight: 600, color: T.fg, fontVariantNumeric: "tabular-nums" },
  chip: {
    borderRadius: 999,
    border: `1px solid ${T.border}`,
    padding: "1px 8px",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: T.mutedFg,
  },
  summary: { marginLeft: "auto", fontSize: 12, color: T.mutedFg },
  grid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0 14px" },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginTop: 6 },
  error: { fontSize: 12, color: T.danger },
  actions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    flexWrap: "wrap",
    margin: "10px 0",
  },
  confirmPrompt: { marginRight: "auto", alignSelf: "center", fontSize: 13, color: T.danger },
  small: { padding: "6px 12px", fontSize: 13 },
  danger: {
    borderRadius: 6,
    border: `1px solid ${T.danger}`,
    background: T.danger,
    color: T.dangerFg,
    fontSize: 13,
    fontFamily: "inherit",
    padding: "6px 12px",
    cursor: "pointer",
    "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
  },
});

/* Intervals carry no name in this modal, so one is derived from the window. A
   name set by hand on the Runner shifts page is left alone. */
const AUTO_NAME = /^Pickups \d{2}:\d{2}-\d{2}:\d{2}$/;

function nameFor(draft, originalName) {
  const auto = `Pickups ${formatTimeOfDay(draft.startTime)}-${formatTimeOfDay(draft.endTime)}`;
  return !originalName || AUTO_NAME.test(originalName) ? auto : originalName;
}

function toDraft(shift) {
  return {
    id: shift.id ?? null,
    name: shift.name ?? "",
    description: shift.description ?? "",
    startTime: shift.startTime ?? DEFAULT_SHIFT.startTime,
    endTime: shift.endTime ?? DEFAULT_SHIFT.endTime,
    gracePeriodMinutes: shift.gracePeriodMinutes ?? DEFAULT_SHIFT.gracePeriodMinutes,
    maxCollectPerSlot: shift.maxCollectPerSlot ?? DEFAULT_SHIFT.maxCollectPerSlot,
    maxParkPerSlot: shift.maxParkPerSlot ?? DEFAULT_SHIFT.maxParkPerSlot,
    slotMinutes: shift.slotMinutes ?? DEFAULT_SHIFT.slotMinutes,
    isActive: shift.isActive ?? true,
  };
}

export default function PickupIntervalRow({ shift, disabled, onSave, onDelete, onCancel }) {
  const s = useStyles();
  const ui = useUi();
  const original = useMemo(() => toDraft(shift), [shift]);
  const [draft, setDraft] = useState(original);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    setDraft(original);
  }, [original]);

  /* Select values arrive as strings; the API takes Int. */
  const setMinutesField = (field) => (event) => {
    const value = parseInt(event.target.value, 10);
    setDraft((current) => ({ ...current, [field]: value }));
  };

  /* An emptied box stays empty rather than becoming NaN; validateShift rejects it. */
  const setCountField = (field) => (event) => {
    const { value } = event.target;
    setDraft((current) => ({ ...current, [field]: value === "" ? "" : Number(value) }));
  };

  // React may release the synthetic event before a functional state updater
  // runs. Capture the primitive now rather than reading event.target inside it.
  const toggleActive = (event) => {
    const { checked } = event.target;
    setDraft((current) => ({ ...current, isActive: checked }));
  };

  const isNew = !original.id;
  const named = { ...draft, name: nameFor(draft, original.name) };
  const overnight = draft.endTime <= draft.startTime;
  const errors = validateShift(named);
  const firstError = Object.values(errors)[0];
  const isDirty = JSON.stringify(draft) !== JSON.stringify(original);
  const isBlocked = disabled || submitting;
  const canSave = !firstError && (isNew || isDirty) && !isBlocked;
  const id = original.id ?? "new";

  const save = async (event) => {
    event.preventDefault();
    if (!canSave) return;

    setSubmitting(true);
    try {
      await onSave(named);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (isBlocked) return;

    setSubmitting(true);
    try {
      await onDelete(original);
    } finally {
      setSubmitting(false);
      setConfirmingDelete(false);
    }
  };

  return (
    <li className={cx(s.card, !draft.isActive && s.inactive)}>
      <form onSubmit={save}>
        <div className={s.head}>
          <span className={s.window}>
            {formatTimeOfDay(draft.startTime)} &ndash; {formatTimeOfDay(draft.endTime)}
          </span>
          {overnight && <span className={s.chip}>Overnight</span>}
          <span className={s.summary}>
            park {draft.maxParkPerSlot || 0} &middot; collect {draft.maxCollectPerSlot || 0} per{" "}
            {draft.slotMinutes} min
          </span>
        </div>

        <div className={s.grid}>
          <FieldRow label="Pickups start" htmlFor={`pi-start-${id}`}>
            <select
              id={`pi-start-${id}`}
              className={ui.field}
              disabled={isBlocked}
              value={draft.startTime}
              onChange={setMinutesField("startTime")}
            >
              {SHIFT_TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </FieldRow>
          <FieldRow label="Pickups end" htmlFor={`pi-end-${id}`}>
            <select
              id={`pi-end-${id}`}
              className={ui.field}
              disabled={isBlocked}
              value={draft.endTime}
              onChange={setMinutesField("endTime")}
            >
              {SHIFT_TIME_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {errors.endTime && <span className={s.error}>{errors.endTime}</span>}
          </FieldRow>
          <FieldRow label="Max pickups per period" htmlFor={`pi-collect-${id}`}>
            <input
              id={`pi-collect-${id}`}
              type="number"
              min="1"
              className={ui.field}
              disabled={isBlocked}
              value={draft.maxCollectPerSlot}
              onChange={setCountField("maxCollectPerSlot")}
            />
            {errors.maxCollectPerSlot && (
              <span className={s.error}>{errors.maxCollectPerSlot}</span>
            )}
          </FieldRow>
          <FieldRow label="Max vehicles to park per period" htmlFor={`pi-park-${id}`}>
            <input
              id={`pi-park-${id}`}
              type="number"
              min="1"
              className={ui.field}
              disabled={isBlocked}
              value={draft.maxParkPerSlot}
              onChange={setCountField("maxParkPerSlot")}
            />
            {errors.maxParkPerSlot && <span className={s.error}>{errors.maxParkPerSlot}</span>}
          </FieldRow>
          <FieldRow label="Period length" htmlFor={`pi-slot-${id}`}>
            <select
              id={`pi-slot-${id}`}
              className={ui.field}
              disabled={isBlocked}
              value={draft.slotMinutes}
              onChange={setMinutesField("slotMinutes")}
            >
              {SLOT_MINUTES_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </FieldRow>
          <FieldRow label="Grace period" htmlFor={`pi-grace-${id}`}>
            <select
              id={`pi-grace-${id}`}
              className={ui.field}
              disabled={isBlocked}
              value={draft.gracePeriodMinutes}
              onChange={setMinutesField("gracePeriodMinutes")}
            >
              {SHIFT_GRACE_PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </FieldRow>
        </div>

        <label className={s.check}>
          <input
            type="checkbox"
            disabled={isBlocked}
            checked={draft.isActive}
            onChange={toggleActive}
          />
          Pickups are taken during this period
        </label>

        <div className={s.actions}>
          {confirmingDelete ? (
            <>
              <span className={s.confirmPrompt}>Delete this period?</span>
              <button
                type="button"
                className={cx(ui.mutedBtn, s.small)}
                disabled={isBlocked}
                onClick={() => setConfirmingDelete(false)}
              >
                Keep
              </button>
              <button type="button" className={s.danger} disabled={isBlocked} onClick={remove}>
                Delete
              </button>
            </>
          ) : (
            <>
              {isNew ? (
                <button type="button" className={cx(ui.mutedBtn, s.small)} onClick={onCancel}>
                  Cancel
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className={cx(ui.mutedBtn, s.small)}
                    disabled={isBlocked}
                    onClick={() => setConfirmingDelete(true)}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className={cx(ui.mutedBtn, s.small)}
                    disabled={!isDirty || isBlocked}
                    onClick={() => setDraft(original)}
                  >
                    Cancel
                  </button>
                </>
              )}
              <button type="submit" className={cx(ui.primaryBtn, s.small)} disabled={!canSave}>
                {isNew ? "Add period" : "Save"}
              </button>
            </>
          )}
        </div>
      </form>
    </li>
  );
}
